import { defineComponent, h, computed } from 'vue'
import { Checkbox, Tooltip, Space, Typography, Tag } from 'ant-design-vue'
import { FormItem } from '@formily/antdv-x3'
import { useField, useFieldSchema } from '@formily/vue'
import { observer } from '@formily/reactive-vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

const { Text } = Typography

/**
 * 判断两个值是否相等（支持基本类型和简单对象/数组）
 */
function isEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a == null || b == null) return a == b
  if (typeof a !== typeof b) return false
  if (typeof a === 'object') {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  return false
}

/**
 * 配置表单项组件
 * 在 FormItem 基础上增加了"使用默认值"的 checkbox 功能
 *
 * 通过 schema 的 x-is-default, x-default-value, x-current-value, x-value-source 来控制显示
 */
export const ConfigFormItem = observer(
  defineComponent({
    name: 'ConfigFormItem',
    inheritAttrs: false,
    setup(props, { slots, attrs }) {
      const fieldRef = useField()
      const schemaRef = useFieldSchema()

      // 从 schema 中获取配置信息
      const schema = computed(() => schemaRef.value || {})
      const isDefault = computed(() => schema.value['x-is-default'] ?? true)
      const defaultValue = computed(() => schema.value['x-default-value'])
      const currentValue = computed(() => schema.value['x-current-value'])
      const valueSource = computed(
        () => schema.value['x-value-source'] || 'default',
      )

      // 值来源的描述和颜色
      const sourceInfo = computed(() => {
        const info: Record<string, { label: string; color: string }> = {
          default: { label: '默认值', color: 'default' },
          global: { label: '全局配置', color: 'blue' },
          file: { label: '配置文件', color: 'green' },
          env: { label: '环境变量', color: 'orange' },
          modify: { label: '动态修改', color: 'red' },
        }
        return info[valueSource.value] || info.default
      })

      // 当 checkbox 变化时，切换使用默认值或当前值
      const handleUseDefaultChange = (e: any) => {
        const checked = e.target?.checked ?? e

        const field = fieldRef.value as any
        if (field) {
          if (checked && defaultValue.value !== undefined) {
            // 使用默认值
            field.setValue?.(defaultValue.value)
          }
          // 如果取消勾选，保持当前输入的值
        }
      }

      return () => {
        const hasDefaultInfo = defaultValue.value !== undefined
        const field = fieldRef.value as any

        // 在渲染时计算是否使用默认值（基于当前字段值）
        // observer 会自动追踪 formily 的响应式依赖
        let useDefaultChecked = isDefault.value
        if (field && defaultValue.value !== undefined) {
          const fieldValue = field.value
          useDefaultChecked = isEqual(fieldValue, defaultValue.value)
        }

        // 构建 extra 内容：显示值来源标签和使用默认值的 checkbox
        const extraContent = hasDefaultInfo ? (
          <Space size={8} style={{ marginTop: '2px' }}>
            <Checkbox
              checked={useDefaultChecked}
              onChange={handleUseDefaultChange}
            >
              <Text type="secondary" style={{ fontSize: '12px' }}>
                使用默认值
              </Text>
            </Checkbox>
            <Tooltip
              title={
                <div>
                  <div>默认值: {String(defaultValue.value)}</div>
                  <div>当前值: {String(currentValue.value)}</div>
                </div>
              }
            >
              <Tag
                color={sourceInfo.value.color}
                style={{ cursor: 'help', fontSize: '11px' }}
              >
                {sourceInfo.value.label}
                <InfoCircleOutlined style={{ marginLeft: '4px' }} />
              </Tag>
            </Tooltip>
          </Space>
        ) : null

        // 合并 attrs 和 extra
        const formItemProps = {
          ...attrs,
          extra: extraContent || (attrs as any).extra,
        }

        return h(FormItem, formItemProps, slots)
      }
    },
  }),
)

export default ConfigFormItem
